/**
 * Gifting & deprived assets for Age Pension-style means tests.
 * Ported verbatim from frontend/src/calculations/giftingDeprivation.js.
 *
 * Uses Australian financial years ending 30 June. Legacy/UI keys `yearYYYY` in `giftsHistory` mean:
 * total gifts in the FY 1 July (YYYY−1) – 30 June YYYY.
 */
import { GIFTING_LIMIT_ANNUAL, GIFTING_LIMIT_5_YEAR } from './constants/ratesAndThresholds.ts';
import type { FactFindGiftsHistory } from './factFindTypes.ts';
import type { StrategyOptions } from './strategyOptionsTypes.ts';

/** Australian FY end year containing `date` (FY is 1 July (E−1) … 30 June E). */
export function getAustralianFYEndingYearContainingDate(date: Date | string): number {
    const d = date instanceof Date ? date : new Date(date);
    const y = d.getFullYear();
    const m = d.getMonth(); // 0 = Jan
    return m >= 6 ? y + 1 : y;
}

/** The dated gifts a gift history carries (`gifts: [{ date, amount }]`), when it does. */
function datedGiftsOf(giftsHistory: Record<string, unknown> | null | undefined): DatedGift[] {
    const list = (giftsHistory as { gifts?: unknown } | null | undefined)?.gifts;
    return Array.isArray(list) ? (list as DatedGift[]) : [];
}

/**
 * Record a gift a plan makes, with its day (24 Oct 2026). A plan's gift is dated so its five years run from
 * the day it is made; the profile's own history is kept by financial year. Plan households are priced, never
 * saved, so the dated list never reaches the stored profile.
 */
export function recordDatedGift(target: { giftsHistory?: Record<string, unknown> | null }, amount: number, date: Date | string): void {
    const n = Math.max(0, Number(amount) || 0);
    const d = giftDay(date);
    if (!(n > 0) || Number.isNaN(d.getTime())) return;
    const history = (target.giftsHistory ?? (target.giftsHistory = { hasGifted: true })) as Record<string, unknown>;
    const gifts = Array.isArray(history.gifts) ? (history.gifts as DatedGift[]) : [];
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    history.gifts = [...gifts, { date: iso, amount: n }];
    history.hasGifted = true;
}

/** Parse giftsHistory + optional strategy gifts into totals per FY end year — dated gifts included, in the year their day falls in. */
export function buildGiftsByFinancialYear(
    giftsHistory: Record<string, unknown> | null | undefined,
    strategyGiftsByFyEnd: Record<string, unknown> = {},
): Map<number, number> {
    const byFy = buildUndatedGiftsByFinancialYear(giftsHistory, strategyGiftsByFyEnd);
    for (const g of datedGiftsOf(giftsHistory)) {
        const d = giftDay(g?.date);
        const n = Math.max(0, Number(g?.amount) || 0);
        if (n > 0 && !Number.isNaN(d.getTime())) {
            const fy = getAustralianFYEndingYearContainingDate(d);
            byFy.set(fy, (byFy.get(fy) || 0) + n);
        }
    }
    return byFy;
}

/** Totals per FY end year of the gifts known only by their financial year (`yearYYYY`, plus strategy FY gifts). */
function buildUndatedGiftsByFinancialYear(
    giftsHistory: Record<string, unknown> | null | undefined,
    strategyGiftsByFyEnd: Record<string, unknown> = {},
): Map<number, number> {
    const byFy = new Map<number, number>();

    const add = (fy: number, amt: unknown) => {
        const n = Math.max(0, Number(amt) || 0);
        if (n <= 0) return;
        byFy.set(fy, (byFy.get(fy) || 0) + n);
    };

    if (giftsHistory && typeof giftsHistory === 'object') {
        for (const key of Object.keys(giftsHistory)) {
            if (!key.startsWith('year')) continue;
            const fy = parseInt(key.slice(4), 10);
            if (!Number.isFinite(fy)) continue;
            add(fy, giftsHistory[key]);
        }
    }

    if (strategyGiftsByFyEnd && typeof strategyGiftsByFyEnd === 'object') {
        for (const [k, v] of Object.entries(strategyGiftsByFyEnd)) {
            const fy = parseInt(k, 10);
            if (Number.isFinite(fy)) add(fy, v);
        }
    }

    return byFy;
}

/** A gift with the day it was made (a strategy's gift, a granny flat right paid above its value). */
export interface DatedGift {
    date: Date | string;
    amount: number;
}

/**
 * One gift as the five-year rule sees it: when it starts counting, when it stops, the order it uses the
 * free areas in, and the financial year its free area comes from.
 */
interface GiftEvent {
    start: number;
    end: number;
    order: number;
    fy: number;
    amount: number;
}

const addYears = (d: Date, years: number) => new Date(d.getFullYear() + years, d.getMonth(), d.getDate());

/**
 * A gift's day as a LOCAL calendar day. `new Date('2026-07-01')` is midnight UTC — 10am on 1 July in Australia —
 * so a gift made on the day an assessment is struck would read as made after it. A `YYYY-MM-DD` (or `YYYY-MM`)
 * string is taken as that day, locally; a Date is used as it stands.
 */
function giftDay(value: Date | string | null | undefined): Date {
    if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(String(value ?? ''));
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, m[3] ? Number(m[3]) : 1);
    const d = new Date(String(value ?? ''));
    return Number.isNaN(d.getTime()) ? d : new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * THE FIVE YEARS RUN FROM THE DAY OF THE GIFT (owner, 24 Oct 2026; Services Australia operational guide
 * 108-06010010-05: "assessed as a deprived asset for 5 years from the date of the gift"). Until then the
 * excess was counted while its FINANCIAL YEAR was within the last five, so a June gift dropped off about a
 * year early.
 *
 * The free areas stay financial-year rules: $10,000 a financial year and $30,000 over a rolling five
 * financial years. They are used in the order the gifts were made, so the first $10,000 given in a year is
 * free and what comes after is the excess; each gift's excess then counts for five years from its own day.
 *
 * A gift the profile records only by financial year (`yearYYYY`) has no day. It is treated as given on
 * 30 June of that year — the conservative reading: counted for the longest the rule allows, five years
 * from that 30 June, and never dropped early. (It counts from the start of that year, and uses the free
 * areas in that order, so a gift recorded for the current year is counted at once.) A dated list,
 * `giftsHistory.gifts: [{ date, amount }]`, is read when present, so the profile can record the day later
 * without this engine changing again.
 */
function giftEvents(byFyUndated: Map<number, number>, dated: ReadonlyArray<DatedGift>): GiftEvent[] {
    const events: GiftEvent[] = [];
    for (const [fy, amount] of byFyUndated) {
        if (!(amount > 0)) continue;
        const fyStart = new Date(fy - 1, 6, 1);
        events.push({ start: fyStart.getTime(), end: new Date(fy + 5, 6, 1).getTime(), order: fyStart.getTime(), fy, amount });
    }
    for (const g of dated) {
        const amount = Math.max(0, Number(g?.amount) || 0);
        const d = giftDay(g?.date);
        if (!(amount > 0) || Number.isNaN(d.getTime())) continue;
        events.push({ start: d.getTime(), end: addYears(d, 5).getTime(), order: d.getTime(), fy: getAustralianFYEndingYearContainingDate(d), amount });
    }
    return events.sort((a, b) => a.order - b.order);
}

/** The excess above the free areas still counted on `asOf`, gift by gift. */
function deprivedOn(events: GiftEvent[], asOf: Date, annualLimit: number, fiveYearLimit: number): number {
    const freeByFy = new Map<number, number>();
    const freeUsed = (from: number, to: number) => {
        let t = 0;
        for (let y = from; y <= to; y++) t += freeByFy.get(y) || 0;
        return t;
    };
    const at = asOf.getTime();
    let counted = 0;
    for (const e of events) {
        const free = Math.min(
            e.amount,
            Math.max(0, annualLimit - (freeByFy.get(e.fy) || 0)),
            Math.max(0, fiveYearLimit - freeUsed(e.fy - 4, e.fy)),
        );
        freeByFy.set(e.fy, (freeByFy.get(e.fy) || 0) + free);
        const excess = e.amount - free;
        if (excess > 0 && e.start <= at && at < e.end) counted += excess;
    }
    return counted;
}

/**
 * THE MONTH OF A YEAR'S GIFTS (owner, 24 Oct 2026). The profile records each financial year's gifts as a total
 * (`yearYYYY`) and, when known, the calendar month they were made (`monthYYYY`, 1–12; with several gifts,
 * the latest). That year's total is dated the LAST day of that month — never dropped early — in the right
 * calendar year: July–December fall in the year before the financial year ends, January–June in that year.
 * With no month ("not sure"), the year is read as given on 30 June, as above.
 */
function monthDatedProfileGifts(giftsHistory: Record<string, unknown> | null | undefined): Array<DatedGift & { fy: number }> {
    const out: Array<DatedGift & { fy: number }> = [];
    if (!giftsHistory || typeof giftsHistory !== 'object') return out;
    for (const key of Object.keys(giftsHistory)) {
        if (!key.startsWith('month')) continue;
        const fy = parseInt(key.slice(5), 10);
        const month = Math.round(Number(giftsHistory[key]));
        const amount = Math.max(0, Number(giftsHistory[`year${fy}`]) || 0);
        if (!Number.isFinite(fy) || !(month >= 1 && month <= 12) || !(amount > 0)) continue;
        const calendarYear = month >= 7 ? fy - 1 : fy;
        out.push({ date: new Date(calendarYear, month, 0), amount, fy }); // day 0 of the next month = the month's last day
    }
    return out;
}

/** Total deprived assets included in assessable assets / deeming for the income test snapshot. */
export function calculateDeprivedAssetsFromGifts(
    giftsHistory: Record<string, unknown> | null | undefined,
    options: {
        asOfDate?: Date;
        /** Gifts known only by the financial year they fell in (keyed by its end year). */
        strategyGiftsByFyEnd?: Record<string, unknown>;
        /** Gifts with the day they were made. */
        datedGifts?: ReadonlyArray<DatedGift>;
        annualLimit?: number;
        fiveYearLimit?: number;
    } = {},
): number {
    const {
        asOfDate = new Date(),
        strategyGiftsByFyEnd = {},
        datedGifts = [],
        annualLimit = GIFTING_LIMIT_ANNUAL,
        fiveYearLimit = GIFTING_LIMIT_5_YEAR,
    } = options;

    const byFy = buildUndatedGiftsByFinancialYear(giftsHistory || {}, strategyGiftsByFyEnd);
    // A year's total the profile dates by month (`monthYYYY`) is dated, not left to the year-end reading.
    const monthDated = monthDatedProfileGifts(giftsHistory);
    for (const g of monthDated) byFy.delete(g.fy);
    // Gifts recorded with their day: a plan's (recordDatedGift), or the profile's dated list.
    const events = giftEvents(byFy, [...monthDated, ...datedGiftsOf(giftsHistory), ...datedGifts]);
    if (events.length === 0) return 0;
    return deprivedOn(events, asOfDate, annualLimit, fiveYearLimit);
}

/**
 * Deprived assets for Age Pension asset test / deeming (gifting free areas, Australian FY), with the
 * gifts a STRATEGY proposes folded in alongside the ones already in the fact-find.
 *
 * The thin strategy-aware wrapper over {@link calculateDeprivedAssetsFromGifts} above: it reads the
 * three places a strategy can propose a gift from (a downsize, an investment-property sale, or the
 * standalone gifting strategy), dates each to `asOfDate` (the day it is made), and hands the combined
 * picture down.
 *
 * Also called directly, outside any means test, by the UI screens that show a client what a gift they
 * are contemplating would cost them (AgedCareAnalyser, AgedCareInsightPanel, RVOtherStrategiesSection),
 * which is why it lives here beside the gifting rules rather than in deemedIncome.ts with the means
 * test that is only one of its callers.
 */
export function calculateDeprivedAssets(
    giftsHistory: FactFindGiftsHistory | undefined,
    strategyOptions: StrategyOptions = {},
    asOfDate: Date = new Date(),
    extraGiftsByFyEnd: Record<string | number, number> | null = null,
    /** Gifts with their day (a granny flat right paid above its value, dated to the arrangement). */
    extraDatedGifts: ReadonlyArray<DatedGift> | null = null,
): number {
    const strategyGiftsByFyEnd: Record<string | number, number> = {};
    // A strategy's gift is made on the day assessed: the five years run from then.
    const datedGifts: DatedGift[] = [...(extraDatedGifts || [])];
    const addStrategyGift = (raw: number) => {
        const n = raw || 0;
        if (n > 0) datedGifts.push({ date: asOfDate, amount: n });
    };
    if (strategyOptions?.downsizeDetails?.giftingAmount) {
        addStrategyGift(strategyOptions.downsizeDetails.giftingAmount);
    }
    if (strategyOptions?.otherStrategies?.gifting && strategyOptions?.otherStrategies?.giftingAmount) {
        addStrategyGift(strategyOptions.otherStrategies.giftingAmount);
    }
    if (strategyOptions?.investmentPropertyDetails?.giftingAmount) {
        addStrategyGift(strategyOptions.investmentPropertyDetails.giftingAmount);
    }
    if (extraGiftsByFyEnd && typeof extraGiftsByFyEnd === 'object') {
        for (const [fy, amt] of Object.entries(extraGiftsByFyEnd)) {
            const n = amt || 0;
            if (n > 0) strategyGiftsByFyEnd[fy] = (strategyGiftsByFyEnd[fy] || 0) + n;
        }
    }

    return calculateDeprivedAssetsFromGifts(giftsHistory, {
        asOfDate,
        strategyGiftsByFyEnd,
        datedGifts,
    });
}
